import type { ActionReducer } from '@ngrx/store';
import { createReducer, on } from '@ngrx/store';

import type { EntityState } from '@core/ngrx-legacy';
import {
  ReportDashboardComponent,
  ReportDashboard,
  ReportDashboardComponentCreate,
} from '@core/reports-types';

import {
  ReportsComponentsActions,
  ReportsComponentsNamespace,
} from './components/components.store';
import { ReportsDashboardsNamespace } from './dashboards/dashboards.store';

type ReportsStore = {
  [ReportsComponentsNamespace]: EntityState<ReportDashboardComponent>;
  [ReportsDashboardsNamespace]: EntityState<ReportDashboard>;
};

const addComponentRef = (
  state: ReportsStore,
  action: {
    payload: {
      data: {
        dashboardId: number;
        component: ReportDashboardComponentCreate | ReportDashboardComponent;
      };
    };
  },
) => {
  const dashboardsState: EntityState<ReportDashboard> = state[ReportsDashboardsNamespace];
  const dashboard = dashboardsState.entityMap[action.payload.data.dashboardId];

  return {
    ...state,
    [ReportsDashboardsNamespace]: {
      ...dashboardsState,
      entityMap: {
        ...dashboardsState.entityMap,
        ...(dashboard
          ? {
              [dashboard.id]: {
                ...dashboard,
                componentIds: [
                  ...(dashboard.componentIds || []),
                  action.payload.data.component.localId,
                ],
              },
            }
          : {}),
      },
    },
  };
};

const removeComponentRef = (
  state: ReportsStore,
  action: {
    dashboardId: number;
    component: ReportDashboardComponent;
  },
) => {
  const dashboardsState: EntityState<ReportDashboard> = state[ReportsDashboardsNamespace];
  const dashboard = dashboardsState.entityMap[action.dashboardId];

  return {
    ...state,
    [ReportsDashboardsNamespace]: {
      ...dashboardsState,
      entityMap: {
        ...dashboardsState.entityMap,
        ...(dashboard
          ? {
              [dashboard.id]: {
                ...dashboard,
                componentIds: dashboard.componentIds.filter(
                  id => !(action.component.localId === id || action.component.id === id),
                ),
              },
            }
          : {}),
      },
    },
  };
};

const reportsReferencesReducer = createReducer<ReportsStore | undefined>(
  undefined,
  on(ReportsComponentsActions.createComponent, (state, action) =>
    addComponentRef(<ReportsStore>state, action),
  ),
  on(ReportsComponentsActions.duplicateComponent, (state, action) =>
    addComponentRef(<ReportsStore>state, action),
  ),
  on(ReportsComponentsActions.removeComponent, (state, action) =>
    removeComponentRef(<ReportsStore>state, action.payload.data),
  ),
  on(ReportsComponentsActions.createOneOptimisticFailed, (state, action) =>
    removeComponentRef(<ReportsStore>state, {
      dashboardId: action.payload.dashboardId,
      component: action.payload.data,
    }),
  ),
);

export function referenceReducer(reducer: ActionReducer<any>): ActionReducer<any> {
  return function (state, action) {
    return reducer(reportsReferencesReducer(state, action), action);
  };
}
